import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Download, Smartphone, Share, CheckCircle2, RefreshCw, WifiOff, Apple, Github } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { AppReleaseCard, detectOS } from "../components/AppReleaseCard";
import { AppReleasePublisher } from "../components/AppReleasePublisher";

/** /app — point d'entrée de l'application installée (PWA / Electron) : redirige selon le rôle. */
export function AppLauncher() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0b0811]"><Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  const role = user.role;
  const roles = user.roles || [];
  if (role === "partner") return <Navigate to="/partner" replace />;
  if (roles.includes("formateur") || role === "tenant_member") return <Navigate to="/formateur/sessions" replace />;
  if (roles.includes("eleve") || role === "client") return <Navigate to="/mes-formations" replace />;
  return <Navigate to="/formateur/sessions" replace />;
}

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; window.dispatchEvent(new Event("pwa-installable")); });
}

const RELEASE_ADMIN_ROLES = ["superadmin", "admin", "tenant_admin"];

/** /admin/formation/app — installer l'application (Windows / Android / macOS / PWA tablettes). */
const APP_META = {
  formation: { title: "Télécharger l'application Blitzz Formation", name: "Blitzz Formation", desc: "Windows, Android, macOS et tablettes — même application, fonctionne hors ligne, mise à jour automatique.", offline: "Sur chaque session, « Préparer hors ligne » télécharge modules, SCORM, stagiaires et messages. Émargement, déverrouillage, progression, notation et messages sont mis en file d'attente." },
  partenaire: { title: "Télécharger l'application Blitzz Partenaire", name: "Blitzz Partenaire", desc: "Dépôt de factures, validation des commandes, commissions et messagerie — sur ordinateur et téléphone.", offline: "Les factures déposées et les messages sont mis en file d'attente hors réseau puis synchronisés." },
  salarie: { title: "Télécharger l'application Blitzz Salarié", name: "Blitzz Salarié", desc: "Notifications, messagerie, mon espace, bulletins, tâches et pointages — sur ordinateur et téléphone, avec mode hors ligne.", offline: "Notifications, messages, tâches, bulletins et documents restent lisibles sans réseau. Messages, tâches et lectures sont mis en file d'attente puis synchronisés." },
};

export default function AppInstallPage({ app = "formation" }) {
  const { user } = useAuth();
  const meta = APP_META[app] || APP_META.formation;
  const [installable, setInstallable] = useState(!!deferredPrompt);
  const [installed, setInstalled] = useState(window.matchMedia?.("(display-mode: standalone)")?.matches || false);
  const [swState, setSwState] = useState("…");
  const [releases, setReleases] = useState({});
  const os = detectOS();
  const githubReleases = process.env.REACT_APP_DESKTOP_RELEASES_URL || "";
  const canPublish = RELEASE_ADMIN_ROLES.includes(user?.role);

  const loadReleases = useCallback(async () => {
    try { const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/app-releases`, { params: { app } }); setReleases(r.data.platforms || {}); } catch { /* hors ligne */ }
  }, [app]);

  useEffect(() => {
    loadReleases();
    const on = () => setInstallable(true);
    window.addEventListener("pwa-installable", on);
    window.addEventListener("appinstalled", () => setInstalled(true));
    navigator.serviceWorker?.getRegistration().then((r) => setSwState(r ? (r.active ? "actif" : "en cours") : "non enregistré")).catch(() => setSwState("indisponible"));
    return () => window.removeEventListener("pwa-installable", on);
  }, [loadReleases]);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const res = await deferredPrompt.userChoice;
    if (res?.outcome === "accepted") setInstalled(true);
    deferredPrompt = null;
    setInstallable(false);
  };

  const pwaButton = installed
    ? <div className="inline-flex items-center gap-2 text-emerald-400 text-sm font-bold" data-testid="install-pwa-done"><CheckCircle2 className="w-4 h-4" /> Application installée</div>
    : installable
      ? <Button onClick={install} className="bg-white/10 text-white font-bold hover:bg-white/20" data-testid="install-pwa-btn"><Download className="w-4 h-4 mr-2" /> Installer la version web (PWA)</Button>
      : null;

  const githubFallback = githubReleases
    ? <a href={githubReleases} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-300 hover:underline" data-testid="release-github-link"><Github className="w-3.5 h-3.5" /> Voir les releases GitHub</a>
    : null;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="app-install-page">
      <PageHeader icon={<Download />} title={meta.title} description={meta.desc} />

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
        <AppReleaseCard platform="windows" release={releases.windows} recommended={os === "windows"} fallback={githubFallback} />
        <AppReleaseCard platform="android" release={releases.android} recommended={os === "android"}
          fallback={pwaButton || <div className="text-xs text-slate-300" data-testid="install-android-hint">En attendant l'APK : dans Chrome, menu <b>⋮</b> → <b>« Installer l'application »</b>.</div>}>
          {releases.android && pwaButton && <div className="pt-1">{pwaButton}</div>}
        </AppReleaseCard>
        <AppReleaseCard platform="mac" release={releases.mac} recommended={os === "mac"} fallback={githubFallback} />

        <div className={`bg-[#14101c] border rounded-2xl p-5 flex flex-col ${os === "ios" ? "border-[#f7c935]/60" : "border-white/10"}`} data-testid="install-pwa-card">
          <div className="flex items-center gap-2 mb-1"><Smartphone className="w-5 h-5 text-[#f7c935]" /><h2 className="font-black text-white">iPad & navigateur</h2></div>
          <p className="text-xs text-slate-400 mb-4 flex-1">Version web installable (PWA) : icône sur l'écran d'accueil, plein écran, hors ligne. Recommandé pour iPad.</p>
          {pwaButton || (os === "ios" ? (
            <div className="text-xs text-slate-300 space-y-1" data-testid="install-ios-hint">
              <div className="font-bold text-white flex items-center gap-1"><Apple className="w-3.5 h-3.5" /> Sur iPad / iPhone (Safari)</div>
              <div>1. Touchez <Share className="w-3 h-3 inline -mt-0.5" /> <b>Partager</b></div>
              <div>2. Choisissez <b>« Sur l'écran d'accueil »</b></div>
              <div>3. Ouvrez « {meta.name} » depuis l'écran d'accueil</div>
            </div>
          ) : (
            <div className="text-xs text-slate-300" data-testid="install-generic-hint">Dans Chrome / Edge : menu <b>⋮</b> → <b>« Installer {meta.name} »</b> (ou l'icône d'installation dans la barre d'adresse).</div>
          ))}
          <div className="mt-4 text-[10px] text-slate-500">Service worker : {swState}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <Feature icon={WifiOff} title="Hors ligne" text={meta.offline} />
        <Feature icon={RefreshCw} title="Synchronisation" text="Dès que le réseau revient, la file d'attente est rejouée automatiquement (ou via le bouton « synchroniser »)." />
        <Feature icon={Download} title="Mises à jour" text="La PWA se met à jour à l'ouverture ; les applications Windows / macOS téléchargent et installent les nouvelles versions publiées." />
      </div>

      {canPublish && <AppReleasePublisher app={app} onChanged={loadReleases} />}
    </div>
  );
}

function Feature({ icon: Icon, title, text }) {
  return (
    <div className="bg-[#0b0811] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1"><Icon className="w-4 h-4 text-[#f7c935]" /><div className="text-sm font-bold text-white">{title}</div></div>
      <p className="text-[11px] text-slate-400">{text}</p>
    </div>
  );
}
